import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import SafeIcon from '../../common/SafeIcon';
import RichTextEditor from './RichTextEditor';

const ResourcesSection = ({ trainerId }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const initialFormState = {
    title: '',
    category: 'General',
    content: '',
    external_url: '',
    is_published: true
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (trainerId) fetchResources();
  }, [trainerId]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('trainer_resources_20250219')
        .select('*')
        .eq('trainer_id', trainerId)
        .order('category', { ascending: true })
        .order('title', { ascending: true });

      if (error) throw error;
      setResources(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        trainer_id: trainerId,
        updated_at: new Date().toISOString()
      };

      if (editingResource) {
        await supabase.from('trainer_resources_20250219').update(payload).eq('id', editingResource.id);
      } else {
        await supabase.from('trainer_resources_20250219').insert([payload]);
      }

      setShowForm(false);
      setEditingResource(null);
      setFormData(initialFormState);
      fetchResources();
    } catch (err) {
      alert('Failed to save resource');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resource? All clients will lose access.')) return;
    try {
      await supabase.from('trainer_resources_20250219').delete().eq('id', id);
      fetchResources();
    } catch (err) {
      alert('Delete failed');
    }
  };

  const filteredResources = resources.filter(r => 
    r.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    r.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-black text-gray-900 leading-tight">Support Library</h2>
          <p className="text-sm text-gray-500 font-medium">Create global guides and FAQs for all clients</p>
        </div>
        <button 
          onClick={() => {
            setEditingResource(null);
            setFormData(initialFormState);
            setShowForm(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-black transition-all flex items-center gap-2 shadow-xl shadow-indigo-100 active:scale-95"
        >
          <SafeIcon name="Plus" /> New Resource
        </button>
      </div>

      <div className="relative">
        <SafeIcon name="Search" className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input 
          type="text" 
          placeholder="Search documentation..." 
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-14 pr-6 py-5 bg-white border-2 border-gray-100 rounded-[2rem] font-bold text-gray-700 shadow-sm outline-none focus:border-indigo-500 transition-all"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredResources.map(resource => (
          <div key={resource.id} className="bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-sm hover:shadow-xl hover:border-indigo-100 transition-all group relative">
            <div className="flex justify-between items-start mb-4">
              <span className="bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase px-2.5 py-1 rounded-lg">
                {resource.category}
              </span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button 
                  onClick={() => {
                    setEditingResource(resource);
                    setFormData(resource);
                    setShowForm(true);
                  }}
                  className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                >
                  <SafeIcon name="Edit" className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDelete(resource.id)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <SafeIcon name="Trash2" className="w-4 h-4" />
                </button>
              </div>
            </div>
            <h3 className="font-black text-xl text-gray-900 mb-2 leading-tight">{resource.title}</h3>
            <p className="text-sm text-gray-400 font-medium line-clamp-2 mb-6">
              {resource.content ? resource.content.replace(/<[^>]*>/g, '') : 'No description provided.'}
            </p>
            <div className="flex items-center justify-between pt-4 border-t border-gray-50">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${resource.is_published ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  {resource.is_published ? 'Published' : 'Draft'}
                </span>
              </div>
              {resource.external_url && (
                <SafeIcon name="Link" className="text-gray-300 w-4 h-4" />
              )}
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {showForm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-[9999]" onClick={() => setShowForm(false)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-[3rem] shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-8 border-b flex justify-between items-center bg-gray-50/50">
                <div>
                  <h3 className="text-2xl font-black text-gray-900">{editingResource ? 'Edit Resource' : 'New Resource'}</h3>
                  <p className="text-sm text-gray-500 font-medium">Create something helpful for your clients</p>
                </div>
                <button onClick={() => setShowForm(false)} className="p-3 hover:bg-gray-200 rounded-2xl transition-colors">
                  <SafeIcon name="X" className="w-6 h-6 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-8 overflow-y-auto flex-1 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Resource Title</label>
                    <input 
                      required 
                      value={formData.title} 
                      onChange={e => setFormData({...formData, title: e.target.value})}
                      className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all"
                      placeholder="e.g. Puppy Biting & Nipping"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Category</label>
                    <input 
                      value={formData.category} 
                      onChange={e => setFormData({...formData, category: e.target.value})}
                      className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all"
                      placeholder="e.g. FAQs, Equipment, Guides"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Content / Body</label>
                  <RichTextEditor 
                    value={formData.content} 
                    onChange={val => setFormData({...formData, content: val})} 
                    placeholder="Provide full details, advice, or instructions..."
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">External Link (Optional)</label>
                  <input 
                    type="url"
                    value={formData.external_url} 
                    onChange={e => setFormData({...formData, external_url: e.target.value})}
                    className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all"
                    placeholder="https://..."
                  />
                </div>

                <div className="bg-indigo-50 p-6 rounded-3xl flex items-center justify-between">
                  <div>
                    <p className="font-black text-indigo-900">Publish Resource</p>
                    <p className="text-xs text-indigo-600 font-medium">Controls if clients can see this in their library</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={formData.is_published}
                      onChange={e => setFormData({...formData, is_published: e.target.checked})}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              </form>

              <div className="p-8 border-t bg-gray-50 flex gap-4">
                <button onClick={handleSubmit} disabled={isSubmitting} className="flex-[2] bg-indigo-600 text-white py-5 rounded-3xl font-black shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2">
                  {isSubmitting ? 'Saving...' : 'Save Resource'}
                </button>
                <button onClick={() => setShowForm(false)} className="flex-1 bg-white border-2 border-gray-100 text-gray-500 py-5 rounded-3xl font-black hover:bg-gray-100 transition-all">Cancel</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResourcesSection;